import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { MentorStoryImage } from "@/lib/api/mentor-stories";
import {
  useDeleteMentorStoryImage,
  useMentorStories,
  useSaveMentorStory,
} from "@/lib/hooks/mentor-stories/use-mentor-stories";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { StorySlideEditor } from "./story-slide-editor";

const MAX_SLIDES = 6;

interface SlideItem {
  id: string;
  key?: string;
  url?: string;
  order: number;
  eyebrow: string | null;
  header: string | null;
}

interface MentorStoriesManagerProps {
  mentorId: string;
}

/** @deprecated Admin editor that writes slides directly. Mentors publish through story review. */
export function MentorStoriesManager({ mentorId }: MentorStoriesManagerProps) {
  const { toast } = useToast();
  const {
    data: existingStory,
    isLoading,
    error,
    refetch,
  } = useMentorStories(mentorId);
  const saveMutation = useSaveMentorStory(mentorId);
  const deleteMutation = useDeleteMentorStoryImage(mentorId);

  const [slides, setSlides] = useState<SlideItem[]>([]);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (existingStory === undefined) return;
    if (existingStory) {
      setSlides(
        [...existingStory.images]
          .sort((a, b) => a.order - b.order)
          .map((img) => ({ ...img, id: img.key })),
      );
    }
    initialized.current = true;
  }, [existingStory]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const addSlide = () => {
    if (slides.length >= MAX_SLIDES) return;
    const newId = `new-${Date.now()}`;
    setSlides((prev) => [
      ...prev,
      { id: newId, order: prev.length, eyebrow: null, header: null },
    ]);
  };

  const updateSlide = (index: number, updated: Partial<MentorStoryImage>) => {
    setSlides((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...updated } : s)),
    );
  };

  const deleteSlide = (index: number) => {
    const slide = slides[index];
    if (slide.key) {
      deleteMutation.mutate(
        { imageKey: slide.key },
        {
          onSuccess: () => {
            setSlides((prev) => prev.filter((s) => s.id !== slide.id));
          },
          onError: () => {
            toast({
              title: "Failed to delete slide",
              description: "Please try again.",
              variant: "destructive",
            });
          },
        },
      );
    } else {
      setSlides((prev) => prev.filter((s) => s.id !== slide.id));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = slides.findIndex((s) => s.id === active.id);
    const newIndex = slides.findIndex((s) => s.id === over.id);

    const reordered = arrayMove(slides, oldIndex, newIndex).map((slide, i) => ({
      ...slide,
      order: i,
    }));
    setSlides(reordered);
  };

  const handleSave = async () => {
    const validSlides = slides.filter(
      (s): s is SlideItem & { key: string; url: string } => !!s.key && !!s.url,
    );

    if (validSlides.length === 0) {
      toast({
        title: "No slides to save",
        description: "Upload at least one image before saving.",
        variant: "destructive",
      });
      return;
    }

    try {
      await saveMutation.mutateAsync({
        storyType: "first_moment",
        images: validSlides.map((s, i) => ({
          key: s.key,
          url: s.url,
          order: i,
          eyebrow: s.eyebrow,
          header: s.header,
        })),
      });
      toast({ title: "Story saved successfully" });
    } catch {
      toast({
        title: "Failed to save story",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <div>
            <h4 className="font-medium">Failed to load mentor stories</h4>
            <p className="mt-1 text-sm">
              There was an error loading the stories. Please try again.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">First Moment Story</h3>
          <p className="text-muted-foreground text-sm">
            Upload slides for this mentor's story ({slides.length}/{MAX_SLIDES})
          </p>
        </div>
        {slides.length < MAX_SLIDES && (
          <Button onClick={addSlide}>
            <Plus className="mr-2 h-4 w-4" />
            Add Slide
          </Button>
        )}
      </div>

      {slides.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-muted-foreground">
            <Plus className="mx-auto mb-4 h-12 w-12 opacity-50" />
            <h4 className="mb-2 text-lg font-medium">No slides yet</h4>
            <p className="mb-4 text-sm">
              Click "Add Slide" to start building this mentor's story.
            </p>
            <Button onClick={addSlide}>
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Slide
            </Button>
          </div>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={slides.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {slides.map((slide, index) => (
                <StorySlideEditor
                  key={slide.id}
                  id={slide.id}
                  mentorId={mentorId}
                  slide={slide}
                  index={index}
                  onUpdate={(updated) => updateSlide(index, updated)}
                  onDelete={() => deleteSlide(index)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {slides.length > 0 && (
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="w-full"
        >
          {saveMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {saveMutation.isPending ? "Saving..." : "Save Story"}
        </Button>
      )}
    </div>
  );
}

